import config from 'config';
import i18n from 'lang/I18n';
import moment from 'moment';
import { dispatch } from 'store';
import { update as updateAuth } from 'store/reducers/auth';
import http from './http';
import { ServerResponse, ServerResponseData, User, UserPassword, VatLookupResult } from './types';

export const get = async (accessToken: string): Promise<User | undefined> => {
    const url = encodeURI(`${config.serverUrl}/user`);
    console.log('USER - GET - URL', url);
    const response: ServerResponse<User> = await http.get(url, {
        headers: { Authorization: `Bearer ${accessToken}` }
    });
    if (!response.data.code) throw response.data;

    const user = response.data.object;

    // Aggiornare i dati di autenticazione (in caso siano diversi da quelli già memorizzati)
    dispatch(
        updateAuth({
            ...user,
            expiryDate: user?.expiryDate && String(user.expiryDate)
        })
    );

    // Conversione delle date da stringhe a date
    // (così il tipo TypeScript è coerente con quello osservato in produzione).
    // Questo perché il tipo ritornato dalla REST per le date è in realtà una stringa.
    if (user?.activationDate) {
        user.activationDate = moment(user.activationDate).toDate();
    }
    if (user?.expiryDate) {
        user.expiryDate = moment(user.expiryDate).toDate();
    }
    if (user?.whatsappActivationDate) {
        user.whatsappActivationDate = moment(user.whatsappActivationDate).toDate();
    }
    if (user?.whatsappExpiryDate) {
        user.whatsappExpiryDate = moment(user.whatsappExpiryDate).toDate();
    }
    if (user?.telegramActivationDate) {
        user.telegramActivationDate = moment(user.telegramActivationDate).toDate();
    }
    if (user?.telegramExpiryDate) {
        user.telegramExpiryDate = moment(user.telegramExpiryDate).toDate();
    }
    return response.data.object;
};

export const update = async (accessToken: string, user: Partial<User & UserPassword>): Promise<User | undefined> => {
    const url = encodeURI(`${config.serverUrl}/user/update`);
    const {
        id,
        username,
        refreshToken,
        accessToken: removedAccessToken,
        skipIdentityCheck,
        documentsVerified,
        identityConfirmed,
        invoiceDataSource,
        ...userData
    } = user;
    void id;
    void username;
    void refreshToken;
    void removedAccessToken;
    void skipIdentityCheck;
    void documentsVerified;
    void identityConfirmed;
    void invoiceDataSource;
    if (!userData.welcomeMessage) {
        userData.welcomeMessage = i18n.t('transcription_default_welcome_message');
    }
    console.log('USER - UPDATE - URL', url);
    console.log('USER - UPDATE - DATA', userData);
    const response: ServerResponse<User> = await http.post(url, userData, {
        headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json'
        }
    });
    if (!response.data.code) throw response.data;
    return response.data.object;
};

export const getVatDetails = async (accessToken: string): Promise<VatLookupResult> => {
    const response = await http.get<ServerResponseData<VatLookupResult>>(`${config.serverUrl}/user/vat-details`, {
        headers: { Authorization: `Bearer ${accessToken}` }
    });
    if (!response.data.code || !response.data.object) throw response.data;
    const payload: unknown = response.data.object;
    if (typeof payload !== 'object' || payload === null || !('success' in payload)) throw response.data;
    const result = payload as { success?: unknown; refreshedFromN8n?: unknown; details?: unknown };
    return {
        success: result.success === true,
        refreshedFromN8n: result.refreshedFromN8n === true,
        details: typeof result.details === 'object' && result.details !== null ? (result.details as Partial<User>) : undefined
    };
};

export const confirmIdentity = async (
    accessToken: string,
    user: Partial<User>,
    identityDocuments: File[],
    companyDocuments: File[] = []
): Promise<User | undefined> => {
    const formData = new FormData();
    formData.append('data', JSON.stringify(user));
    identityDocuments.forEach((document) => formData.append('identityDocument', document));
    companyDocuments.forEach((document) => formData.append('companyDocument', document));

    const response: ServerResponse<User> = await http.post(`${config.serverUrl}/user/identity/confirm`, formData, {
        headers: { Authorization: `Bearer ${accessToken}` },
        timeout: 30000
    });
    if (!response.data.code) throw response.data;
    return response.data.object;
};
